import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  Building2,
  Clock,
  HeartPulse,
  LayoutGrid,
  LineChart,
  Package,
  Palette,
  Printer,
  ScrollText,
  Shirt,
  Target,
  Users,
  UserPlus,
  ExternalLink,
  X,
} from "lucide-react";
import type * as API from "@shared-core/data/api-types";
import {
  formatCurrencyGbpCompact,
  formatCount,
  formatDateOrDash,
} from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { Card } from "../../primitives/card";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import { Input } from "../../primitives/input";
import { Textarea } from "../../primitives/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { CustomerStrategyBanner } from "./CustomerStrategyBanner";
import {
  ClickableStat,
  FactorDetailList,
  FactorList,
  IntentCardHeader,
  type CardFactor,
  type Tone,
} from "./IntentCardPrimitives";
import {
  ACCOUNT_HEALTH_TOTAL_WEIGHT,
  COVERAGE_TONE,
  coverageBadge,
  HEALTH_TIER_LABEL,
  HEALTH_TIER_VARIANT,
  healthScoreTier,
  OVERRIDABLE_HEALTH_TIER_KEYS,
  resolveAccountHealthFactors,
  toneFromVariant,
  untappedTier,
  type OverridableHealthTierKey,
} from "./strategyTiers";
import { currentTime } from "@shared-core/domain/current-time";

// ────────────────────────────────────────────────────────────────
// Shared label / tone maps
// ────────────────────────────────────────────────────────────────

const CATEGORY_LABEL: Record<API.ContactCategory, string> = {
  strategic: "strategic",
  key: "key",
  operational: "operational",
  unknown: "unknown",
};

const CONTACT_CATEGORY_ORDER: API.ContactCategory[] = [
  "strategic",
  "key",
  "operational",
  "unknown",
];

const COVERAGE_LABEL: Record<API.CoverageLevel, string> = {
  absent: "unknown",
  patchy: "patchy",
  adequate: "adequate",
  good: "good",
};

// ────────────────────────────────────────────────────────────────
// Opportunity card
// ────────────────────────────────────────────────────────────────

const WALLET_FAMILIES: Array<{
  label: string;
  amountKey: keyof API.CustomerStrategicIntelligenceView;
  rationaleKey: keyof API.CustomerStrategicIntelligenceView;
  icon: React.ReactNode;
}> = [
  {
    label: "Commercial print & mailing",
    amountKey: "commercial_print_and_mailing_estimated_spend_gbp",
    rationaleKey: "commercial_print_and_mailing_estimated_spend_rationale",
    icon: <Printer />,
  },
  {
    label: "Workwear",
    amountKey: "workwear_estimated_spend_gbp",
    rationaleKey: "workwear_estimated_spend_rationale",
    icon: <Shirt />,
  },
  {
    label: "Merch",
    amountKey: "merch_estimated_spend_gbp",
    rationaleKey: "merch_estimated_spend_rationale",
    icon: <Package />,
  },
  {
    label: "Creative work",
    amountKey: "creative_work_estimated_spend_gbp",
    rationaleKey: "creative_work_estimated_spend_rationale",
    icon: <Palette />,
  },
];

interface OpportunityCardProps {
  customerId: string;
  strategic: API.CustomerStrategicIntelligenceView | null | undefined;
  walletGbp: number | null;
  revenue12MGbp: number;
  pendingOpportunityCount: number;
}

function OpportunityCard({
  customerId,
  strategic,
  walletGbp,
  revenue12MGbp,
  pendingOpportunityCount,
}: OpportunityCardProps) {
  const [dialogOpen, setDialogOpen] = useState(false);

  const captured = walletGbp != null ? Math.min(revenue12MGbp, walletGbp) : 0;
  const untapped =
    walletGbp != null ? Math.max(walletGbp - revenue12MGbp, 0) : null;
  const untappedTierInfo = untappedTier(untapped);
  const sharePct =
    walletGbp != null && walletGbp > 0
      ? Math.round((revenue12MGbp / walletGbp) * 100)
      : null;

  // Factor rows: 2 buying-posture descriptors + 4 wallet categories.
  // Same data drives the card body (label/value) and the dialog
  // (label/value + rationale narrative).
  const factors: CardFactor[] = [];
  if (strategic?.centralised_print_control_appetite) {
    factors.push({
      label: "Central control need",
      value: strategic.centralised_print_control_appetite,
      narrative: strategic.centralised_print_control_rationale ?? undefined,
      icon: <Building2 />,
    });
  }
  if (strategic?.contracted_relationship_appetite) {
    factors.push({
      label: "Contractual certainty need",
      value: strategic.contracted_relationship_appetite,
      narrative: strategic.contracted_relationship_rationale ?? undefined,
      icon: <ScrollText />,
    });
  }
  for (const family of WALLET_FAMILIES) {
    const amount = strategic?.[family.amountKey] as number | null | undefined;
    const rationale = strategic?.[family.rationaleKey] as
      string | null | undefined;
    factors.push({
      label: family.label,
      value: amount != null ? formatCurrencyGbpCompact(amount) : "—",
      narrative: rationale ?? undefined,
      icon: family.icon,
    });
  }

  return (
    <>
      <Card className="p-5 gap-3 flex flex-col shadow-md hover:shadow-lg transition-shadow">
        <button
          type="button"
          onClick={() => setDialogOpen(true)}
          className="appearance-none bg-transparent border-0 block w-[calc(100%+1.5rem)] text-left rounded-lg -mx-3 -mt-3 px-3 pt-3 pb-2 space-y-3 cursor-pointer hover:bg-muted/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <IntentCardHeader
            label="Opportunity"
            hero={
              untapped != null ? (
                <span>
                  {formatCurrencyGbpCompact(untapped)}{" "}
                  <span className="text-base text-muted-foreground font-normal">
                    untapped
                  </span>
                </span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )
            }
            badge={
              untappedTierInfo && (
                <Badge variant={untappedTierInfo.variant}>
                  {untappedTierInfo.label}
                </Badge>
              )
            }
            icon={<Target className="size-5" />}
            tone="primary"
          />
          {factors.length > 0 && <FactorList factors={factors} />}
        </button>

        <div className="pt-3 mt-auto border-t border-border">
          <ClickableStat
            label="Recommendations"
            value={formatCount(pendingOpportunityCount)}
            to={`${paths.recommendations()}?customer=${customerId}`}
            muted={pendingOpportunityCount === 0}
            centered
          />
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-[95vw] sm:max-w-5xl max-h-[90vh] flex flex-col gap-4">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Target className="size-5 text-primary" />
              Opportunity
              {untappedTierInfo && (
                <Badge variant={untappedTierInfo.variant} className="ml-1">
                  {untappedTierInfo.label}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {walletGbp != null
                ? `${formatCurrencyGbpCompact(captured)} of ${formatCurrencyGbpCompact(walletGbp)} est. wallet captured (${sharePct ?? 0}%).`
                : "Wallet not yet estimated."}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 min-h-0 space-y-5 text-sm">
            {untapped != null && (
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                  Untapped wallet
                </div>
                <div className="text-2xl font-semibold">
                  {formatCurrencyGbpCompact(untapped)}
                </div>
              </div>
            )}
            {factors.length > 0 && <FactorDetailList factors={factors} />}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// Relationship depth card
// ────────────────────────────────────────────────────────────────

interface RelationshipDepthCardProps {
  customerId: string;
  strategic: API.CustomerStrategicIntelligenceView | null | undefined;
  contacts: API.EnrichedContact[];
  prospects: API.ProspectContact[];
}

function countsByCategory(
  contacts: API.EnrichedContact[],
): Record<API.ContactCategory, number> {
  const counts: Record<API.ContactCategory, number> = {
    strategic: 0,
    key: 0,
    operational: 0,
    unknown: 0,
  };
  for (const c of contacts) {
    const cat = (c.contact_category ?? "unknown") as API.ContactCategory;
    counts[cat] += 1;
  }
  return counts;
}

/**
 * Picks the "top 3" preview rows for a category. Prefers contacts with a
 * job title (more informative in a compact preview); falls back to the
 * first 3 if fewer than 3 have titles. Stable order within each bucket is
 * provided by the caller's pre-filter.
 */
function pickTopPreview(
  rows: API.EnrichedContact[],
  limit: number = 3,
): API.EnrichedContact[] {
  const withTitles = rows.filter((c) => (c.job_title ?? "").trim().length > 0);
  const ordered = [
    ...withTitles,
    ...rows.filter((c) => !withTitles.includes(c)),
  ];
  return ordered.slice(0, limit);
}

/**
 * A single prospect row inside the relationship dialog. Owns the
 * inline "bad suggestion" reason prompt state so each row's prompt is
 * independent. ``onSuppress`` is optional — when omitted (stories /
 * read-only contexts), the X button is not rendered.
 */
function ProspectRow({
  prospect,
  onSuppress,
}: {
  prospect: API.ProspectContact;
  onSuppress?: (prospectId: string, reasonNote: string | null) => void;
}) {
  const [promptOpen, setPromptOpen] = useState(false);
  const [reason, setReason] = useState("");

  const commit = () => {
    if (onSuppress) {
      onSuppress(prospect.id, reason.trim() ? reason.trim() : null);
    }
    setPromptOpen(false);
    setReason("");
  };

  const cancel = () => {
    setPromptOpen(false);
    setReason("");
  };

  return (
    <div className="min-w-0">
      <div className="flex items-baseline gap-1.5">
        <UserPlus className="size-3 text-muted-foreground shrink-0 translate-y-0.5" />
        <div className="min-w-0 flex-1">
          <span className="font-medium text-muted-foreground">
            {prospect.name}
          </span>
          {prospect.likely_job_title && (
            <span className="text-muted-foreground font-normal">
              {" "}
              · {prospect.likely_job_title}
            </span>
          )}
          {prospect.linkedin_url && (
            <a
              href={prospect.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 inline-flex items-center text-primary hover:underline"
            >
              <ExternalLink className="size-3" />
            </a>
          )}
          {prospect.rationale && (
            <div className="text-muted-foreground text-xs leading-relaxed mt-0.5">
              {prospect.rationale}
            </div>
          )}
        </div>
        {onSuppress && !promptOpen && (
          <button
            type="button"
            onClick={() => setPromptOpen(true)}
            title="Bad suggestion — hide and stop proposing"
            aria-label="Mark as bad suggestion"
            className="shrink-0 text-muted-foreground hover:text-destructive transition-colors"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>
      {promptOpen && (
        <div className="mt-2 ml-[1.125rem] flex items-center gap-2">
          <input
            type="text"
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commit();
              } else if (e.key === "Escape") {
                e.preventDefault();
                cancel();
              }
            }}
            placeholder="Optional reason (Enter to confirm, Esc to cancel)"
            className="flex-1 rounded-md border border-border bg-background px-2 py-1 text-xs"
          />
          <button
            type="button"
            onClick={commit}
            className="text-xs text-primary hover:underline"
          >
            Suppress
          </button>
          <button
            type="button"
            onClick={cancel}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

function RelationshipSummaryTable({
  customerId,
  contacts,
  counts,
  prospects,
  onSuppressProspect,
}: {
  customerId: string;
  contacts: API.EnrichedContact[];
  counts: Record<API.ContactCategory, number>;
  prospects: API.ProspectContact[];
  onSuppressProspect?: (prospectId: string, reasonNote: string | null) => void;
}) {
  const contactsByCategory: Record<API.ContactCategory, API.EnrichedContact[]> =
    { strategic: [], key: [], operational: [], unknown: [] };
  for (const c of contacts) {
    const cat = (c.contact_category ?? "unknown") as API.ContactCategory;
    contactsByCategory[cat].push(c);
  }

  const prospectsByCategory: Record<
    API.ContactCategory,
    API.ProspectContact[]
  > = { strategic: [], key: [], operational: [], unknown: [] };
  for (const p of prospects) {
    const cat = (p.contact_category ?? "unknown") as API.ContactCategory;
    prospectsByCategory[cat].push(p);
  }

  return (
    <div className="overflow-hidden rounded-md border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-muted-foreground bg-muted/40">
            <th className="text-left font-medium py-2 px-3 w-32">Category</th>
            <th className="text-right font-medium py-2 px-3 w-16">Count</th>
            <th className="text-left font-medium py-2 px-3">
              Contacts and prospects
            </th>
          </tr>
        </thead>
        <tbody>
          {CONTACT_CATEGORY_ORDER.map((cat) => {
            const catRows = contactsByCategory[cat];
            const catCount = counts[cat];
            const catPreview = pickTopPreview(catRows);
            const showContactsHref = `${paths.contacts()}?customer=${customerId}&category=${cat}`;

            const prospectRows = prospectsByCategory[cat];
            const prospectCount = prospectRows.length;
            const prospectPreview = prospectRows.slice(0, 3);
            const showProspectsHref = `${paths.prospects()}?customer=${customerId}`;

            return (
              <tr key={cat} className="border-t border-border align-top">
                <td className="py-3 px-3 font-medium">
                  {CATEGORY_LABEL[cat].charAt(0).toUpperCase() +
                    CATEGORY_LABEL[cat].slice(1)}
                </td>
                <td className="py-3 px-3 text-right font-medium tabular-nums">
                  {catCount}
                </td>
                <td className="py-3 px-3">
                  <div className="flex flex-col gap-2">
                    {/* Contacts block — always rendered; "—" when empty. */}
                    {catCount === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <div className="flex flex-col gap-1">
                        {catPreview.map((contact) => (
                          <div key={contact.id} className="min-w-0">
                            <span className="font-medium">{contact.name}</span>
                            {contact.job_title && (
                              <span className="text-muted-foreground font-normal">
                                {" "}
                                · {contact.job_title}
                              </span>
                            )}
                            {contact.linkedin_url && (
                              <a
                                href={contact.linkedin_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-2 inline-flex items-center text-primary hover:underline"
                              >
                                <ExternalLink className="size-3" />
                              </a>
                            )}
                          </div>
                        ))}
                        {catCount > catPreview.length && (
                          <div className="text-xs text-muted-foreground">
                            +{catCount - catPreview.length} more
                          </div>
                        )}
                        <a
                          href={showContactsHref}
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1"
                        >
                          Show all contacts <ExternalLink className="size-3" />
                        </a>
                      </div>
                    )}

                    {/* Prospects block — soft-tinted sub-block with UserPlus
                        icon + muted names marks these out as "suggested, not
                        in the contact book yet". */}
                    {prospectCount > 0 && (
                      <div className="flex flex-col gap-1 rounded-md bg-muted/40 p-2 mt-1">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
                          Prospects
                        </div>
                        {prospectPreview.map((p) => (
                          <ProspectRow
                            key={p.id}
                            prospect={p}
                            onSuppress={onSuppressProspect}
                          />
                        ))}
                        {prospectCount > prospectPreview.length && (
                          <div className="text-xs text-muted-foreground pl-[1.125rem]">
                            +{prospectCount - prospectPreview.length} more
                          </div>
                        )}
                        <a
                          href={showProspectsHref}
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1 pl-[1.125rem]"
                        >
                          Show all prospects <ExternalLink className="size-3" />
                        </a>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RelationshipDepthCard({
  customerId,
  strategic,
  contacts,
  prospects,
}: RelationshipDepthCardProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const adapter = useDataAdapter();
  const [suppressedIds, setSuppressedIds] = useState<Set<string>>(
    () => new Set(),
  );

  const visibleProspects = useMemo(
    () => prospects.filter((p) => !suppressedIds.has(p.id)),
    [prospects, suppressedIds],
  );

  const handleSuppress = useCallback(
    async (prospectId: string, reasonNote: string | null) => {
      // Optimistic removal; rollback on error.
      setSuppressedIds((prev) => {
        const next = new Set(prev);
        next.add(prospectId);
        return next;
      });
      try {
        await adapter.suppressProspect(prospectId, reasonNote ?? undefined);
      } catch (err) {
        setSuppressedIds((prev) => {
          const next = new Set(prev);
          next.delete(prospectId);
          return next;
        });
        toast.error("Couldn't suppress prospect — please try again.");
        console.error("[prospect suppress] failed:", err);
      }
    },
    [adapter],
  );

  const counts = useMemo(() => countsByCategory(contacts), [contacts]);
  const total = contacts.length;
  const coverage = strategic?.existing_contact_coverage_quality as
    API.CoverageLevel | null | undefined;
  const coverageTone: Tone = coverage ? COVERAGE_TONE[coverage] : "muted";

  const prospectCount = visibleProspects.length;

  // Factor rows: one per contact category. Same data on card and dialog.
  const factors: CardFactor[] = CONTACT_CATEGORY_ORDER.map((cat) => ({
    label:
      CATEGORY_LABEL[cat].charAt(0).toUpperCase() +
      CATEGORY_LABEL[cat].slice(1),
    value: counts[cat],
  }));

  // Badge falls back to "Unknown" when coverage hasn't been computed; the
  // "absent" level also surfaces as "Unknown" (see COVERAGE_BADGE map) so
  // sales people see a single "we don't know yet" state.
  const displayBadge = coverageBadge(coverage) ?? {
    label: "Unknown",
    variant: "neutral" as const,
  };

  return (
    <>
      <Card className="p-5 gap-3 flex flex-col shadow-md hover:shadow-lg transition-shadow">
        <button
          type="button"
          onClick={() => setDialogOpen(true)}
          className="appearance-none bg-transparent border-0 block w-[calc(100%+1.5rem)] text-left rounded-lg -mx-3 -mt-3 px-3 pt-3 pb-2 space-y-3 cursor-pointer hover:bg-muted/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <IntentCardHeader
            label="Relationship depth"
            hero={
              <span>
                {counts.strategic + counts.key}{" "}
                <span className="text-base text-muted-foreground font-normal">
                  strategic &amp; key contacts
                </span>
              </span>
            }
            badge={
              <Badge variant={displayBadge.variant}>{displayBadge.label}</Badge>
            }
            icon={<Users className="size-5" />}
            tone={coverageTone}
          />
          <FactorList factors={factors} />
        </button>

        <div className="pt-3 mt-auto border-t border-border">
          <ClickableStat
            label="Prospects"
            value={formatCount(prospectCount)}
            to={`${paths.prospects()}?customer=${customerId}`}
            muted={prospectCount === 0}
            centered
          />
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-[95vw] sm:max-w-5xl max-h-[90vh] flex flex-col gap-4">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Users className="size-5 text-primary" />
              Relationship depth
              <Badge variant={displayBadge.variant} className="ml-1">
                {displayBadge.label}
              </Badge>
            </DialogTitle>
            {coverage && (
              <DialogDescription>
                Coverage:{" "}
                <span className="font-medium capitalize">
                  {COVERAGE_LABEL[coverage]}
                </span>{" "}
                · {counts.strategic} strategic · {counts.key} key ·{" "}
                {counts.operational} operational · {counts.unknown} unknown (of{" "}
                {total} total) · {prospectCount}{" "}
                {prospectCount === 1 ? "prospect" : "prospects"}
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="overflow-y-auto flex-1 min-h-0 space-y-6 text-sm">
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                Strategic &amp; key contacts
              </div>
              <div className="text-2xl font-semibold">
                {counts.strategic + counts.key}
              </div>
            </div>
            {strategic?.existing_contact_coverage_rationale && (
              <p className="leading-relaxed">
                {strategic.existing_contact_coverage_rationale}
              </p>
            )}

            {(contacts.length > 0 || visibleProspects.length > 0) && (
              <RelationshipSummaryTable
                customerId={customerId}
                contacts={contacts}
                counts={counts}
                prospects={visibleProspects}
                onSuppressProspect={handleSuppress}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// Account health card
// ────────────────────────────────────────────────────────────────
//
// Five factor scores come from backend signals (family: account_health)
// seeded via scripts/bulk_seed_account_health.yaml. The tile reads
// them, computes a weighted composite for the hero, and bands the
// composite into a tier badge.
//
// Weights and tier bands live on the frontend as a presentation
// concern — the same thresholds are mirrored in the backend signal
// impact bands so tile colour/label tracks the backend impact.

const ACCOUNT_HEALTH_ICONS: Record<string, React.ReactNode> = {
  "Sales trend": <LineChart />,
  "Buying recency": <Clock />,
  "Contact coverage": <Users />,
  "Product breadth": <LayoutGrid />,
  "Risk flags": <AlertTriangle />,
};

interface AccountHealthCardProps {
  customerId: string;
  strategic: API.CustomerStrategicIntelligenceView | null | undefined;
  /** Signals filtered to signal_family === 'account_health' */
  accountHealthSignals: API.Signal[];
  /** Count for the card-footer "Alerts" quick link — not a score input. */
  openAlertCount: number;
  /** Active rep-set manual override, if any. */
  healthOverride?: API.CustomerHealthTierOverride | null;
  /** Set (or replace) the manual override. Rethrows on failure so the
   * dialog form can show the error inline and stay open. */
  onSetHealthOverride?: (
    overrideTier: API.HealthTierOverrideValue,
    reason: string,
    expiresAt: string,
  ) => Promise<void>;
  /** Clear an active override before it expires. */
  onClearHealthOverride?: () => Promise<void>;
}

function AccountHealthCard({
  customerId,
  strategic,
  accountHealthSignals,
  openAlertCount,
  healthOverride,
  onSetHealthOverride,
  onClearHealthOverride,
}: AccountHealthCardProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [overrideFormTier, setOverrideFormTier] =
    useState<OverridableHealthTierKey>("watch");
  const [overrideFormReason, setOverrideFormReason] = useState("");
  const [overrideFormExpiresAt, setOverrideFormExpiresAt] = useState("");
  const [overrideFormError, setOverrideFormError] = useState<string | null>(
    null,
  );
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);
  const [clearingOverride, setClearingOverride] = useState(false);

  const factors = useMemo(
    () => resolveAccountHealthFactors(accountHealthSignals),
    [accountHealthSignals],
  );

  const overallSignal = accountHealthSignals.find(
    (s) => s.signal_name === "Account health: overall score",
  );
  const healthScore = overallSignal
    ? (() => {
        const v = Number.parseInt(overallSignal.signal_value, 10);
        return Number.isFinite(v) ? v : null;
      })()
    : null;
  const isOverridden = Boolean(healthOverride);
  const tier = isOverridden
    ? {
        label: HEALTH_TIER_LABEL[healthOverride!.override_tier],
        variant: HEALTH_TIER_VARIANT[healthOverride!.override_tier],
      }
    : healthScoreTier(healthScore);
  const cardTone: Tone = toneFromVariant(tier?.variant);

  const handleSubmitOverride = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!onSetHealthOverride) return;
    if (!overrideFormReason.trim()) {
      setOverrideFormError("A reason is required.");
      return;
    }
    if (!overrideFormExpiresAt) {
      setOverrideFormError("An expiry date is required.");
      return;
    }
    const expiresAtIso = new Date(
      `${overrideFormExpiresAt}T23:59:59`,
    ).toISOString();
    if (new Date(expiresAtIso) <= currentTime()) {
      setOverrideFormError("Expiry must be in the future.");
      return;
    }
    setOverrideFormError(null);
    setOverrideSubmitting(true);
    try {
      await onSetHealthOverride(
        overrideFormTier,
        overrideFormReason.trim(),
        expiresAtIso,
      );
      setOverrideFormReason("");
      setOverrideFormExpiresAt("");
    } catch (err) {
      console.error("[health override] set failed:", err);
      setOverrideFormError("Couldn't save the override — please try again.");
    } finally {
      setOverrideSubmitting(false);
    }
  };

  const handleClearOverride = async () => {
    if (!onClearHealthOverride) return;
    setClearingOverride(true);
    try {
      await onClearHealthOverride();
    } catch (err) {
      console.error("[health override] clear failed:", err);
      setOverrideFormError("Couldn't clear the override — please try again.");
    } finally {
      setClearingOverride(false);
    }
  };

  const cardFactors: CardFactor[] = factors.map((f) => ({
    label: f.label,
    value: f.score ?? "—",
  }));
  const dialogFactors: CardFactor[] = factors.map((f) => ({
    label: f.label,
    value: f.score ?? "—",
    narrative: f.narrative,
    weight: f.weight,
    contribution:
      f.score !== null
        ? (f.score * f.weight) / ACCOUNT_HEALTH_TOTAL_WEIGHT
        : undefined,
    icon: ACCOUNT_HEALTH_ICONS[f.label],
  }));

  return (
    <>
      <Card className="p-5 gap-3 flex flex-col shadow-md hover:shadow-lg transition-shadow">
        <button
          type="button"
          onClick={() => setDialogOpen(true)}
          className="appearance-none bg-transparent border-0 block w-[calc(100%+1.5rem)] text-left rounded-lg -mx-3 -mt-3 px-3 pt-3 pb-2 space-y-3 cursor-pointer hover:bg-muted/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <IntentCardHeader
            label="Account health"
            hero={
              <span>
                {healthScore != null ? `${healthScore}%` : "—"}{" "}
                <span className="text-base text-muted-foreground font-normal">
                  health score
                </span>
              </span>
            }
            badge={
              tier ? (
                <Badge
                  variant={tier.variant}
                  title={isOverridden ? "Manually overridden" : undefined}
                >
                  {tier.label}
                  {isOverridden ? <span aria-hidden="true"> ✎</span> : null}
                </Badge>
              ) : (
                <Badge variant="neutral">Unknown</Badge>
              )
            }
            icon={<HeartPulse className="size-5" />}
            tone={cardTone}
          />
          <FactorList factors={cardFactors} />
        </button>

        <div className="pt-3 mt-auto border-t border-border">
          <ClickableStat
            label="Alerts"
            value={formatCount(openAlertCount)}
            to={`${paths.alerts()}?customer=${customerId}`}
            muted={openAlertCount === 0}
            centered
          />
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-[95vw] sm:max-w-md max-h-[90vh] flex flex-col gap-4">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <HeartPulse className="size-5 text-primary" />
              Account health
              <Badge variant={tier ? tier.variant : "neutral"} className="ml-1">
                {tier ? tier.label : "Unknown"}
                {isOverridden ? <span aria-hidden="true"> ✎</span> : null}
              </Badge>
            </DialogTitle>
            <DialogDescription>
              {healthScore != null
                ? `${healthScore}% health score`
                : "Health score not yet available"}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 min-h-0 space-y-5 text-sm">
            {healthScore != null && (
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                  Health score
                </div>
                <div className="text-2xl font-semibold">{healthScore}%</div>
              </div>
            )}
            {strategic?.preserve_and_protect_rationale && (
              <p className="leading-relaxed">
                {strategic.preserve_and_protect_rationale}
              </p>
            )}
            <section>
              <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                Factor breakdown
              </h3>
              <FactorDetailList factors={dialogFactors} showWeights />
            </section>

            {(onSetHealthOverride || onClearHealthOverride) && (
              <section className="space-y-3 border-t border-border pt-4">
                <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
                  Manual override
                </h3>

                {healthOverride && (
                  <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm space-y-2">
                    <p>
                      <span className="font-medium">
                        Overridden — expires{" "}
                        {formatDateOrDash(healthOverride.expires_at)}
                      </span>
                      : {healthOverride.reason}
                    </p>
                    {onClearHealthOverride && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleClearOverride}
                        disabled={clearingOverride}
                      >
                        {clearingOverride ? "Clearing…" : "Clear override"}
                      </Button>
                    )}
                  </div>
                )}

                {onSetHealthOverride && (
                  <form onSubmit={handleSubmitOverride} className="space-y-2.5">
                    <p className="text-xs text-muted-foreground">
                      I know why this looks {tier ? tier.label : "off"} —
                      it&apos;s not. Set the tier a rep believes reflects
                      reality, with a reason and an expiry.
                    </p>
                    <div className="grid grid-cols-2 gap-2.5">
                      <div>
                        <label className="block text-xs mb-1 text-muted-foreground">
                          Tier
                        </label>
                        <Select
                          value={overrideFormTier}
                          onValueChange={(v) =>
                            setOverrideFormTier(v as OverridableHealthTierKey)
                          }
                        >
                          <SelectTrigger className="w-full" size="sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {OVERRIDABLE_HEALTH_TIER_KEYS.map((k) => (
                              <SelectItem key={k} value={k}>
                                {HEALTH_TIER_LABEL[k]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-xs mb-1 text-muted-foreground">
                          Expires
                        </label>
                        <Input
                          type="date"
                          value={overrideFormExpiresAt}
                          onChange={(e) =>
                            setOverrideFormExpiresAt(e.target.value)
                          }
                          className="h-8 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs mb-1 text-muted-foreground">
                        Reason
                      </label>
                      <Textarea
                        value={overrideFormReason}
                        onChange={(e) => setOverrideFormReason(e.target.value)}
                        placeholder="e.g. one big deferred order skews the sales-trend signal"
                        rows={2}
                        className="text-sm"
                      />
                    </div>
                    {overrideFormError && (
                      <p className="text-xs text-destructive">
                        {overrideFormError}
                      </p>
                    )}
                    <Button
                      type="submit"
                      size="sm"
                      disabled={overrideSubmitting}
                    >
                      {overrideSubmitting
                        ? "Saving…"
                        : healthOverride
                          ? "Replace override"
                          : "Set override"}
                    </Button>
                  </form>
                )}
              </section>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// Pyramid composition — surface-tinted container binding banner + cards
// ────────────────────────────────────────────────────────────────

export interface CustomerStrategyPyramidProps {
  customer: API.CustomerIntelligenceDetail;
  contacts: API.EnrichedContact[];
  prospects: API.ProspectContact[];
  pendingOpportunityCount: number;
  openAlertCount: number;
  /** Set (or replace) the customer's manual account-health tier override. */
  onSetHealthOverride?: (
    overrideTier: API.HealthTierOverrideValue,
    reason: string,
    expiresAt: string,
  ) => Promise<void>;
  /** Clear an active manual override before it expires. */
  onClearHealthOverride?: () => Promise<void>;
}

/**
 * The apex of the customer-page information architecture. A surface-tinted
 * container holds the Strategy banner at the top with Opportunity /
 * Relationship depth / Account health cards beneath — the visual
 * relationship reads as "Strategy synthesises the three below."
 */
export function CustomerStrategyPyramid({
  customer,
  contacts,
  prospects,
  pendingOpportunityCount,
  openAlertCount,
  onSetHealthOverride,
  onClearHealthOverride,
}: CustomerStrategyPyramidProps) {
  const customerId = customer.customer.id;
  const strategic = customer.strategic_intelligence ?? null;
  const metrics = customer.metrics;
  const revenue12M = metrics?.revenue_last_12_months ?? 0;
  const walletGbp = customer.uk_wallet_gbp ?? null;

  const accountHealthSignals = useMemo(
    () =>
      (customer.signals ?? []).filter(
        (s): s is API.Signal => s.signal_family === "account_health",
      ),
    [customer.signals],
  );

  return (
    <section
      aria-label="Strategy"
      className="rounded-xl border border-border bg-card p-5 space-y-5"
    >
      <CustomerStrategyBanner
        strategic={strategic}
        walletGbp={walletGbp}
        revenue12MGbp={revenue12M}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <OpportunityCard
          customerId={customerId}
          strategic={strategic}
          walletGbp={walletGbp}
          revenue12MGbp={revenue12M}
          pendingOpportunityCount={pendingOpportunityCount}
        />
        <RelationshipDepthCard
          customerId={customerId}
          strategic={strategic}
          contacts={contacts}
          prospects={prospects}
        />
        <AccountHealthCard
          customerId={customerId}
          strategic={strategic}
          accountHealthSignals={accountHealthSignals}
          openAlertCount={openAlertCount}
          healthOverride={customer.health_tier_override}
          onSetHealthOverride={onSetHealthOverride}
          onClearHealthOverride={onClearHealthOverride}
        />
      </div>
    </section>
  );
}
